import { ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { Observable } from 'rxjs';
import { KeycloakService } from 'keycloak-angular';

// Adjust these two relative imports if the services live elsewhere in your feature tree.
import { DataAssetConfigurationService } from '../services/data-asset-configuration';
import { KeycloakStateService } from 'src/app/core/service/utility.service';
import {
  WorkflowNavigationService
} from '../../shared/workflow-navigation.service';
import { ProcessCompleteDialogComponent } from 'src/app/shared/dialogs/process-complete-dialog/process-complete-dialog.component';
import { MatDialog } from '@angular/material/dialog';

export interface ConfiguredDropdownOption {
  id: string;
  name: string;
  description?: string;
}

type DropdownKey = 'regime' | 'dataClassification' | 'assetRiskLevel' | 'dataAssetType';

interface DataAssetConfigurationRecord {
  configurationId?: string | null;
  assetId?: string | null;
  organizationId?: string | null;
  assetName?: string | null;
  assetDescription?: string | null;
  riskValue?: number | string | null;
  businessValue?: number | string | null;
  dataClassificationId?: string | null;
  dataClassificationIds?: string[] | null;
  assetRiskLevelId?: string | null;
  dataAssetTypeId?: string | null;
  dataAssetTypeIds?: string[] | null;
  regimeId?: string | null;
  regimeIds?: string[] | null;
  dataClassificationName?: string | null;
  dataClassificationNames?: string[] | string | null;
  assetRiskLevelName?: string | null;
  dataAssetTypeName?: string | null;
  dataAssetTypeNames?: string[] | string | null;
  regimeName?: string | null;
  regimeNames?: string[] | string | null;
  configurationStatus?: string | null;
  configuredBy?: string | null;
  configuredByKeycloakId?: string | null;
  [key: string]: unknown;
}

@Component({
  selector: 'app-configure-data-asset-by-id',
  templateUrl: './configure-data-asset-by-id.component.html',
  styleUrls: ['./configure-data-asset-by-id.component.scss']
})
export class ConfigureDataAssetByIdComponent implements OnInit {
  form: FormGroup;

  /** The route parameter is currently named `assetsourceId` in the supplied route.
   * It is used as the dataAssetId expected by FetchDataAssetConfigurationById().
   */
  dataAssetId = '';
  configurationStatus = 'NOT_CONFIGURED';
  organizationId = '';
  configuredBy = '';
  configuredByKeycloakId = '';

  record: DataAssetConfigurationRecord | null = null;
  loading = true;
  saving = false;
  loadError = '';
  saveError = '';
  successMessage = '';

  dropdownLoading: Record<DropdownKey, boolean> = {
    regime: false,
    dataClassification: false,
    assetRiskLevel: false,
    dataAssetType: false
  };
  dropdownErrors: Record<DropdownKey, string> = {
    regime: '',
    dataClassification: '',
    assetRiskLevel: '',
    dataAssetType: ''
  };
  dropdownOptions: Record<DropdownKey, ConfiguredDropdownOption[]> = {
    regime: [],
    dataClassification: [],
    assetRiskLevel: [],
    dataAssetType: []
  };

  creatingOptionFor: DropdownKey | null = null;
  newOptionName = '';
  creatingOption = false;
  createOptionError = '';

  constructor(
    private readonly fb: FormBuilder,
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly configurationService: DataAssetConfigurationService,
    private readonly keycloakStateService: KeycloakStateService,
    private readonly keycloakService: KeycloakService,
    private readonly cdr: ChangeDetectorRef,
    private readonly workflowNavigation: WorkflowNavigationService,
    private readonly dialog: MatDialog
  ) {
    this.form = this.fb.group({
      // All configuration fields are intentionally optional; classification, type and regime use ID arrays.
      riskValue: [''],
      businessValue: [''],
      dataClassificationIds: [[]],
      assetRiskLevelId: [''],
      dataAssetTypeIds: [[]],
      regimeIds: [[]]
    });
  }

  async ngOnInit(): Promise<void> {
    const params = this.route.snapshot.paramMap;
    this.dataAssetId =
      params.get('assetId') ||
      '';

    this.configurationStatus = (
      params.get('configurationStatus') || 'NOT_CONFIGURED'
    ).trim().toUpperCase();

    this.organizationId = this.keycloakStateService.getOrganizationId() || '';
    this.configuredByKeycloakId =
      this.keycloakStateService.getUserId() || this.getKeycloakClaim('sub');
    this.configuredBy = this.getConfiguredByFromKeycloak();

    if (!this.dataAssetId) {
      this.loading = false;
      this.loadError = 'The data asset ID is missing from the route.';
      return;
    }

    await this.loadConfiguration();
    if (!this.record) {
      this.loading = false;
      this.cdr.markForCheck();
      return;
    }

    // The selected record is a fallback if KeycloakStateService has not populated org ID yet.
    this.organizationId = this.organizationId || String(this.record.organizationId || '');
    this.configurationStatus = (
      this.configurationStatus || String(this.record.configurationStatus || 'NOT_CONFIGURED')
    ).toUpperCase();

    await Promise.all([
      this.loadDropdownOptions('regime'),
      this.loadDropdownOptions('dataClassification'),
      this.loadDropdownOptions('assetRiskLevel'),
      this.loadDropdownOptions('dataAssetType')
    ]);

    this.patchFormFromRecord();
    this.ensureRecordOptionsExist();
    this.loading = false;
    this.cdr.markForCheck();
  }

  get pageTitle(): string {
    return this.configurationStatus === 'CONFIGURED'
      ? 'Update Data Asset Configuration'
      : 'Configure Data Asset';
  }

  get submitLabel(): string {
    return this.configurationStatus === 'CONFIGURED'
      ? 'Update Configuration'
      : 'Save Configuration';
  }

  get hasDropdownLoadError(): boolean {
    return Object.values(this.dropdownErrors).some(Boolean);
  }

  async loadConfiguration(): Promise<void> {
    this.loadError = '';
    this.loading = true;
    try {
      const response: unknown = await 
        this.configurationService.FetchDataAssetConfigurationById(this.dataAssetId).toPromise()
      
      const record = this.normalizeRecord(response);
      if (!record) {
        this.loadError = 'No configuration details were returned for this data asset.';
        this.record = null;
        return;
      }
      this.record = record;
      // Prefer the status sent by the caller, but fall back to the response if it was omitted.
      const routeStatus = this.route.snapshot.paramMap.get('configurationStatus');
      this.configurationStatus = String(routeStatus || record.configurationStatus || 'NOT_CONFIGURED').toUpperCase();
      this.organizationId = this.organizationId || String(record.organizationId || '');
      this.configuredBy = this.configuredBy || String(record.configuredBy || '');
      this.configuredByKeycloakId = this.configuredByKeycloakId || String(record.configuredByKeycloakId || '');
    } catch (error) {
      this.record = null;
      this.loadError = this.getErrorMessage(error, 'Unable to load the data asset configuration.');
    } finally {
      this.loading = false;
      this.cdr.markForCheck();
    }
  }

  async retryLoad(): Promise<void> {
    await this.loadConfiguration();
    if (!this.record) return;
    this.organizationId = this.organizationId || String(this.record.organizationId || '');
    await Promise.all([
      this.loadDropdownOptions('regime'),
      this.loadDropdownOptions('dataClassification'),
      this.loadDropdownOptions('assetRiskLevel'),
      this.loadDropdownOptions('dataAssetType')
    ]);
    this.patchFormFromRecord();
    this.ensureRecordOptionsExist();
    this.loading = false;
    this.cdr.markForCheck();
  }

  async loadDropdownOptions(kind: DropdownKey): Promise<void> {
    if (!this.organizationId) {
      this.dropdownErrors[kind] = 'Organization information is unavailable. Refresh the page after signing in again.';
      return;
    }

    this.dropdownLoading[kind] = true;
    this.dropdownErrors[kind] = '';
    try {
      let request: Observable<unknown>;
      switch (kind) {
        case 'regime':
          request = this.configurationService.FetchConfiguredDropdownsRegime(this.organizationId);
          break;
        case 'dataClassification':
          request = this.configurationService.FetchConfiguredDropdownsDataClassification(this.organizationId);
          break;
        case 'assetRiskLevel':
          request = this.configurationService.FetchConfiguredDropdownsAssetRiskLevel(this.organizationId);
          break;
        case 'dataAssetType':
          request = this.configurationService.FetchConfiguredDropdownsDataAssetType(this.organizationId);
          break;
      }

      const response: unknown = await request.toPromise();
      this.dropdownOptions[kind] = this.normalizeOptions(response, kind);
    } catch (error) {
      this.dropdownErrors[kind] = this.getErrorMessage(error, `Unable to load ${this.getDropdownLabel(kind)} options.`);
    } finally {
      this.dropdownLoading[kind] = false;
      this.cdr.markForCheck();
    }
  }

  beginCreateOption(kind: DropdownKey): void {
    this.creatingOptionFor = kind;
    this.newOptionName = '';
    this.createOptionError = '';
  }

  cancelCreateOption(): void {
    this.creatingOptionFor = null;
    this.newOptionName = '';
    this.createOptionError = '';
  }

  async createDropdownOption(): Promise<void> {
    const kind = this.creatingOptionFor;
    const name = this.newOptionName.trim();
    if (!kind) return;
    if (!name) {
      this.createOptionError = 'Enter a name for the new option.';
      return;
    }
    if (!this.organizationId) {
      this.createOptionError = 'Organization information is unavailable, so the option cannot be created.';
      return;
    }

    const duplicate = this.dropdownOptions[kind].some(
      option => option.name.trim().toLocaleLowerCase() === name.toLocaleLowerCase()
    );
    if (duplicate) {
      this.createOptionError = 'An option with this name already exists.';
      return;
    }

    this.creatingOption = true;
    this.createOptionError = '';
    try {
      let request: Observable<unknown>;
      switch (kind) {
        case 'regime':
          request = this.configurationService.CreateConfiguredDropdownsRegime(name, this.organizationId);
          break;
        case 'dataClassification':
          request = this.configurationService.CreateConfiguredDropdownsDataClassification(name, this.organizationId);
          break;
        case 'assetRiskLevel':
          request = this.configurationService.CreateConfiguredDropdownsAssetRiskLevel(name, this.organizationId);
          break;
        case 'dataAssetType':
          request = this.configurationService.CreateConfiguredDropdownsDataAssetType(name, this.organizationId);
          break;
      }

      const createResponse: unknown = await request.toPromise();
      const createdOptions = this.normalizeOptions(createResponse, kind);
      // Some create endpoints may return a single entity, while others may return a wrapper.
      const createdOption = createdOptions.find(option => option.name.toLocaleLowerCase() === name.toLocaleLowerCase());

      // Re-fetch so the dropdown remains consistent with the backend catalog.
      await this.loadDropdownOptions(kind);
      let optionToSelect = this.dropdownOptions[kind].find(
        option => option.name.toLocaleLowerCase() === name.toLocaleLowerCase()
      );

      if (!optionToSelect && createdOption) {
        this.dropdownOptions[kind] = [...this.dropdownOptions[kind], createdOption];
        optionToSelect = createdOption;
      }
      if (!optionToSelect) {
        // Use the create response when the backend does not immediately expose the new row in fetch.
        const raw = this.unwrapSingle(createResponse);
        const rawRecord = raw && typeof raw === 'object' ? raw as Record<string, unknown> : null;
        const id = rawRecord?.['id'] ?? rawRecord?.['value'] ?? rawRecord?.['uuid'];
        if (id) {
          optionToSelect = { id: String(id), name };
          this.dropdownOptions[kind] = [...this.dropdownOptions[kind], optionToSelect];
        }
      }

      if (optionToSelect) {
        this.selectCreatedOption(kind, optionToSelect.id);
      } else {
        this.createOptionError = 'The option was created, but its ID was not returned by the API. Refresh the dropdown and select it manually.';
      }

      if (!this.createOptionError) {
        this.creatingOptionFor = null;
        this.newOptionName = '';
      }
    } catch (error) {
      this.createOptionError = this.getErrorMessage(error, `Unable to create ${this.getDropdownLabel(kind)} option.`);
    } finally {
      this.creatingOption = false;
      this.cdr.markForCheck();
    }
  }

  async saveConfiguration(): Promise<void> {
    if (!this.dataAssetId || this.saving) return;

    this.saving = true;
    this.saveError = '';
    this.successMessage = '';

    const value = this.form.getRawValue();
    // The service currently declares these numeric fields as strings; blank optional values are sent as ''.
    const riskValue = this.toOptionalString(value.riskValue);
    const businessValue = this.toOptionalString(value.businessValue);
    const dataClassificationIds = this.toStringArray(value.dataClassificationIds);
    const assetRiskLevelId = this.toOptionalString(value.assetRiskLevelId);
    const dataAssetTypeIds = this.toStringArray(value.dataAssetTypeIds);
    const regimeIds = this.toStringArray(value.regimeIds);

    if (!this.configuredByKeycloakId) {
      this.configuredByKeycloakId = this.getKeycloakClaim('sub');
    }
    if (!this.configuredBy) {
      this.configuredBy = this.getConfiguredByFromKeycloak();
    }

    try {
      const request = this.configurationStatus === 'CONFIGURED'
        ? this.configurationService.ConfigureDataAssetForConfiguredStatus(
            this.dataAssetId, riskValue, businessValue, dataClassificationIds,
            assetRiskLevelId, dataAssetTypeIds, regimeIds,
            this.configuredBy, this.configuredByKeycloakId
          )
        : this.configurationService.ConfigureDataAssetForNotConfiguredStatus(
            this.dataAssetId, riskValue, businessValue, dataClassificationIds,
            assetRiskLevelId, dataAssetTypeIds, regimeIds,
            this.configuredBy, this.configuredByKeycloakId
          );

      await request.toPromise();
      this.successMessage = this.configurationStatus === 'CONFIGURED'
        ? 'The data asset configuration has been updated successfully.' 
        : 'The data asset has been configured successfully.';
     

      if(this.configurationStatus==='CONFIGURED'){
         this.showConfigurationUpdateDialog();
      }
      else{
         this.showConfigurationDialog();
      }
        

         this.configurationStatus = 'CONFIGURED';

      this.navigateToDataAssetConfiguration();
    } catch (error) {
      this.saveError = this.getErrorMessage(error, 'Unable to save the data asset configuration. Please try again.');
    } finally {
      this.saving = false;
      this.cdr.markForCheck();
    }
  }

    showConfigurationDialog(): void {
    this.dialog.open(ProcessCompleteDialogComponent, {
      data: {
        title: 'Data Asset Configured',
        message: 'The data asset has been configured successfully.',
      },
        width: 'min(32rem, 94vw)',
        maxWidth: '94vw',
        autoFocus: 'first-tabbable',
        restoreFocus: true,
      });
    }

     showConfigurationUpdateDialog(): void {
    this.dialog.open(ProcessCompleteDialogComponent, {
      data: {
        title: 'Data Asset Configuration Updated',
        message: 'The data asset configuration has been updated successfully.',
      },
        width: 'min(32rem, 94vw)',
        maxWidth: '94vw',
        autoFocus: 'first-tabbable',
        restoreFocus: true,
      });
    }

  backToConfigurations(): void {
  void this.workflowNavigation.navigateToWorkflow(
    'dataAssetConfiguration'
  );
  }

  navigateToDataAssetConfiguration(): void {
  void this.workflowNavigation.navigateToWorkflow(
    'dataAssetConfiguration'
  );
  } 

  getDropdownLabel(kind: DropdownKey): string {
    const labels: Record<DropdownKey, string> = {
      regime: 'Regime',
      dataClassification: 'Data classification',
      assetRiskLevel: 'Asset risk level',
      dataAssetType: 'Data asset type'
    };
    return labels[kind];
  }

  private patchFormFromRecord(): void {
    if (!this.record) return;

    // Resolve selections against the real dropdown catalog first. Some API versions
    // return legacy singular fields, while the newer contract uses plural arrays.
    // Names are a last-resort fallback if the server doesn't provide usable IDs.
    const dataClassificationIds = this.resolveRecordSelectionIds(
      'dataClassification',
      this.record.dataClassificationIds,
      this.record.dataClassificationId,
      this.record.dataClassificationNames,
      this.record.dataClassificationName
    );
    const dataAssetTypeIds = this.resolveRecordSelectionIds(
      'dataAssetType',
      this.record.dataAssetTypeIds,
      this.record.dataAssetTypeId,
      this.record.dataAssetTypeNames,
      this.record.dataAssetTypeName
    );
    const regimeIds = this.resolveRecordSelectionIds(
      'regime',
      this.record.regimeIds,
      this.record.regimeId,
      this.record.regimeNames,
      this.record.regimeName
    );

    this.form.patchValue({
      riskValue: this.record.riskValue ?? '',
      businessValue: this.record.businessValue ?? '',
      dataClassificationIds,
      assetRiskLevelId: this.resolveSingleRecordSelectionId(
        this.record.assetRiskLevelId,
        this.record.assetRiskLevelName
      ),
      dataAssetTypeIds,
      regimeIds
    }, { emitEvent: false });

    this.cdr.markForCheck();
  }

  private ensureRecordOptionsExist(): void {
    if (!this.record) return;

    this.addRecordOptions(
      'regime',
      this.recordIds(this.record.regimeIds, this.record.regimeId),
      this.recordNames(this.record.regimeNames, this.record.regimeName)
    );
    this.addRecordOptions(
      'dataClassification',
      this.recordIds(this.record.dataClassificationIds, this.record.dataClassificationId),
      this.recordNames(this.record.dataClassificationNames, this.record.dataClassificationName)
    );
    this.addRecordOption('assetRiskLevel', this.record.assetRiskLevelId, this.record.assetRiskLevelName);
    this.addRecordOptions(
      'dataAssetType',
      this.recordIds(this.record.dataAssetTypeIds, this.record.dataAssetTypeId),
      this.recordNames(this.record.dataAssetTypeNames, this.record.dataAssetTypeName)
    );
  }

  private addRecordOptions(kind: DropdownKey, ids: string[], names: string[]): void {
    ids.forEach((id, index) => this.addRecordOption(kind, id, names[index] || id));
  }

  private addRecordOption(kind: DropdownKey, id?: string | null, name?: string | null): void {
    if (!id) return;
    const normalizedId = String(id).trim();
    if (!normalizedId) return;
    const exists = this.dropdownOptions[kind].some(option => this.sameId(option.id, normalizedId));
    if (!exists) {
      this.dropdownOptions[kind] = [...this.dropdownOptions[kind], { id: normalizedId, name: name?.trim() || normalizedId }];
    }
  }

  private normalizeRecord(payload: unknown): DataAssetConfigurationRecord | null {
    const unwrapped = this.unwrapSingle(payload);
    if (!unwrapped || typeof unwrapped !== 'object') return null;
    const candidate = unwrapped as Record<string, unknown>;
    if (!('assetId' in candidate) && !('configurationId' in candidate) && !('assetName' in candidate)) {
      return null;
    }
    return candidate as DataAssetConfigurationRecord;
  }

  private normalizeOptions(payload: unknown, kind: DropdownKey): ConfiguredDropdownOption[] {
    let value: unknown = payload;
    for (let depth = 0; depth < 4; depth++) {
      if (Array.isArray(value)) break;
      if (!value || typeof value !== 'object') return [];
      const object = value as Record<string, unknown>;
      const wrapped = object['data'] ?? object['items'] ?? object['content'] ??
        object['results'] ?? object['records'] ?? object['options'];
      if (wrapped === undefined) {
        value = [object];
        break;
      }
      value = wrapped;
    }
    if (!Array.isArray(value)) return [];

    // Dropdown endpoints may return resource-specific properties (for example,
    // dataClassificationId/dataClassificationName) instead of generic id/name.
    // Normalize those fields before binding selections to the form controls.
    const idFields: Record<DropdownKey, string[]> = {
      dataClassification: [
        'dataClassificationId', 'classificationId', 'dataClassificationID',
        'data_classification_id', 'id', 'value', 'uuid'
      ],
      assetRiskLevel: [
        'assetRiskLevelId', 'riskLevelId', 'assetRiskLevelID',
        'asset_risk_level_id', 'id', 'value', 'uuid'
      ],
      dataAssetType: [
        'dataAssetTypeId', 'assetTypeId', 'dataAssetTypeID',
        'data_asset_type_id', 'id', 'value', 'uuid'
      ],
      regime: [
        'regimeId', 'regulationId', 'regimeID', 'regime_id',
        'id', 'value', 'uuid'
      ]
    };
    const nameFields: Record<DropdownKey, string[]> = {
      dataClassification: [
        'dataClassificationName', 'classificationName', 'dataClassification',
        'data_classification_name', 'name', 'label', 'displayName'
      ],
      assetRiskLevel: [
        'assetRiskLevelName', 'riskLevelName', 'assetRiskLevel',
        'asset_risk_level_name', 'name', 'label', 'displayName'
      ],
      dataAssetType: [
        'dataAssetTypeName', 'assetTypeName', 'dataAssetType',
        'data_asset_type_name', 'name', 'label', 'displayName'
      ],
      regime: [
        'regimeName', 'regulationName', 'regime', 'regime_name',
        'name', 'label', 'displayName'
      ]
    };

    const firstValue = (record: Record<string, unknown>, keys: string[]): unknown => {
      for (const key of keys) {
        const candidate = record[key];
        if (candidate !== null && candidate !== undefined && String(candidate).trim() !== '') {
          return candidate;
        }
      }
      return '';
    };

    return value
      .filter(item => item && typeof item === 'object')
      .map(item => {
        const option = item as Record<string, unknown>;
        const id = firstValue(option, idFields[kind]);
        const name = firstValue(option, nameFields[kind]);
        return {
          id: String(id).trim(),
          name: String(name).trim(),
          description: option['description'] == null ? undefined : String(option['description'])
        };
      })
      .filter(option => Boolean(option.id && option.name))
      .sort((a, b) => a.name.localeCompare(b.name));
  }

  private unwrapSingle(payload: unknown): unknown {
    let value = payload;
    for (let depth = 0; depth < 3; depth++) {
      if (Array.isArray(value)) return value[0] ?? null;
      if (!value || typeof value !== 'object') return value;
      const object = value as Record<string, unknown>;
      const wrapped = object['data'] ?? object['item'] ?? object['result'] ?? object['configuration'] ?? object['assetConfiguration'];
      if (wrapped === undefined) return value;
      value = wrapped;
    }
    return value;
  }

  private formControlFor(kind: DropdownKey): string {
    const controls: Record<DropdownKey, string> = {
      regime: 'regimeIds',
      dataClassification: 'dataClassificationIds',
      assetRiskLevel: 'assetRiskLevelId',
      dataAssetType: 'dataAssetTypeIds'
    };
    return controls[kind];
  }

  isMultiSelectKind(kind: DropdownKey): boolean {
    return kind === 'regime' || kind === 'dataClassification' || kind === 'dataAssetType';
  }

  isOptionSelected(kind: DropdownKey, optionId: string): boolean {
    const value = this.form.get(this.formControlFor(kind))?.value;
    if (!this.isMultiSelectKind(kind)) return this.sameId(value, optionId);
    return this.toStringArray(value).some(id => this.sameId(id, optionId));
  }

  toggleMultiSelect(kind: DropdownKey, optionId: string, event: Event): void {
    if (!this.isMultiSelectKind(kind)) return;
    const input = event.target as HTMLInputElement | null;
    const control = this.form.get(this.formControlFor(kind));
    if (!input || !control) return;

    const selected = this.toStringArray(control.value);
    const next = input.checked
      ? (selected.includes(optionId) ? selected : [...selected, optionId])
      : selected.filter(id => id !== optionId);

    control.setValue(next);
    control.markAsDirty();
    control.markAsTouched();
    this.cdr.markForCheck();
  }

  private selectCreatedOption(kind: DropdownKey, optionId: string): void {
    const controlName = this.formControlFor(kind);
    if (this.isMultiSelectKind(kind)) {
      const current = this.toStringArray(this.form.get(controlName)?.value);
      this.form.patchValue({ [controlName]: current.includes(optionId) ? current : [...current, optionId] });
      return;
    }
    this.form.patchValue({ [controlName]: optionId });
  }

  private recordIds(pluralValue: unknown, singularValue: unknown): string[] {
    // Prefer a populated plural array, but don't discard a valid legacy singular ID
    // when the backend returns an empty [] for the plural field.
    const pluralIds = this.toStringArray(pluralValue);
    return pluralIds.length ? pluralIds : this.toStringArray(singularValue);
  }

  private recordNames(pluralValue: unknown, singularValue: unknown): string[] {
    // Prefer populated plural names but keep compatibility with legacy singular names.
    const pluralNames = this.toStringArray(pluralValue);
    return pluralNames.length ? pluralNames : this.toStringArray(singularValue);
  }

  private resolveRecordSelectionIds(
    kind: 'regime' | 'dataClassification' | 'dataAssetType',
    pluralIds: unknown,
    singularId: unknown,
    pluralNames: unknown,
    singularName: unknown
  ): string[] {
    const savedIds = this.recordIds(pluralIds, singularId);
    if (savedIds.length) {
      // Use the catalog's exact ID spelling where possible so bindings match exactly.
      return savedIds.map(id =>
        this.dropdownOptions[kind].find(option => this.sameId(option.id, id))?.id ?? id
      );
    }

    // Some older response shapes may return names without their associated IDs.
    // Resolve those names to catalog IDs rather than silently leaving the selection blank.
    const savedNames = this.recordNames(pluralNames, singularName);
    const resolvedIds = savedNames.map(name =>
      this.dropdownOptions[kind].find(option => this.sameName(option.name, name))?.id ?? ''
    ).filter(Boolean);

    return this.uniqueStrings(resolvedIds);
  }

  private resolveSingleRecordSelectionId(idValue: unknown, nameValue: unknown): string {
    const savedId = this.toOptionalString(idValue);
    if (savedId) {
      const matchedById = this.dropdownOptions.assetRiskLevel.find(option => this.sameId(option.id, savedId));
      if (matchedById) return matchedById.id;
      // Preserve IDs from the configuration API even if they're temporarily absent
      // from the dropdown catalog; ensureRecordOptionsExist() will add a fallback option.
      return savedId;
    }

    const savedName = this.toOptionalString(nameValue);
    return this.dropdownOptions.assetRiskLevel.find(option => this.sameName(option.name, savedName))?.id ?? '';
  }

  private sameId(left: unknown, right: unknown): boolean {
    return String(left ?? '').trim().toLocaleLowerCase() === String(right ?? '').trim().toLocaleLowerCase();
  }

  private sameName(left: unknown, right: unknown): boolean {
    return String(left ?? '').trim().toLocaleLowerCase() === String(right ?? '').trim().toLocaleLowerCase();
  }

  private uniqueStrings(values: string[]): string[] {
    return [...new Set(values.map(value => String(value ?? '').trim()).filter(Boolean))];
  }

  private toStringArray(value: unknown): string[] {
    const values = Array.isArray(value)
      ? value
      : (value === null || value === undefined || value === '' ? [] : [value]);

    return this.uniqueStrings(values.map(item => {
      if (item && typeof item === 'object') {
        const object = item as Record<string, unknown>;
        const idValue = object['id'] ?? object['value'] ?? object['uuid'] ??
          object['dataClassificationId'] ?? object['dataAssetTypeId'] ??
          object['regimeId'] ?? object['assetRiskLevelId'];
        return idValue == null ? '' : String(idValue).trim();
      }
      return String(item ?? '').trim();
    }));
  }

  private toOptionalString(value: unknown): string {
    if (value === null || value === undefined) return '';
    return String(value).trim();
  }

  private getKeycloakClaim(claim: string): string {
    try {
      const parsed = (this.keycloakService.getKeycloakInstance()?.tokenParsed || {}) as Record<string, unknown>;
      return parsed[claim] == null ? '' : String(parsed[claim]);
    } catch {
      return '';
    }
  }

  private getConfiguredByFromKeycloak(): string {
    try {
      const parsed = (this.keycloakService.getKeycloakInstance()?.tokenParsed || {}) as Record<string, unknown>;
      const preferred = parsed['preferred_username'] || parsed['name'] || parsed['email'];
      return preferred == null ? '' : String(preferred);
    } catch {
      return '';
    }
  }

  private getErrorMessage(error: unknown, fallback: string): string {
    if (typeof error === 'string' && error.trim()) return error;
    if (error && typeof error === 'object') {
      const value = error as Record<string, unknown>;
      const nested = value['error'];
      if (nested && typeof nested === 'object') {
        const message = (nested as Record<string, unknown>)['message'];
        if (message) return String(message);
      }
      if (value['message']) return String(value['message']);
    }
    return fallback;
  }
}
